import { twMerge } from 'tailwind-merge';
import useTranslation from 'next-translate/useTranslation';
import Button from '@/components/common/Button';
import Progress from '@/components/common/Progress';
import { ClickTargetType, ClickType } from '@/constants/track';
import SharePopup from '@/components/common/SharePopup';
import ShareReferralImage from '@/components/common/SharePopup/shareReferralImage';
import { useEffect, useRef, useState } from 'react';
import useClickActionT2 from '@/tracks/useClickActionT2';
import { getSchoolShare } from '@/api/customer';
import { getReferralIdFromCookie } from '@/utils/common/referral';
import { isEmptyObject } from '@/utils/isEmpty';
import { CroppedImage } from '@weee-fe/module';

const ExclusiveEvent = ({ className, data = {} }: { className?: string; data?: any }) => {
  const { t } = useTranslation('student');
  const shareImageRef: any = useRef();
  const { shareClick } = useClickActionT2();

  const [shareData, setShareData] = useState<any>(null);

  const { school_campaign } = data;
  const { now_number, max_number, progress_bar_main_color, progress_bar_background_color, content, school_key, icon } =
    school_campaign || {};

  const getShareData = async () => {
    if (!school_key) return;
    const res = await getSchoolShare({ channel: school_key, referral_id: getReferralIdFromCookie() });
    if (res?.result) {
      setShareData(res?.object);
    } else {
      setShareData(null);
    }
  };

  useEffect(() => {
    getShareData();
  }, []);

  if (isEmptyObject(school_campaign)) return null;

  return (
    <div className={twMerge('px-5 pt-2 pb-4 bg-surface-100-bg', className)}>
      <div className="pt-2 pb-[14px]">
        <span className="enki-body-sm-medium text-surface-100-fg-default">{content?.title}</span>
      </div>

      <div className="bg-surface-100-bg rounded-xl py-4 px-[14px] enki-elevation-5 border border-solid border-surface-100-hairline">
        {/* 标题 */}
        {content?.sub_title && (
          <div className="flex items-center mb-2">
            {icon && (
              <CroppedImage
                url={icon}
                alt="icon check"
                configs={{ renderWidth: 24, renderHeight: 24 }}
                wrapperClassName="w-6 h-6 mr-2 flex-none"
                className="w-full h-full object-cover"
              />
            )}
            <div
              className="enki-body-sm-medium text-primary-1 flex-1"
              dangerouslySetInnerHTML={{ __html: content?.sub_title }}></div>
          </div>
        )}

        {/* 进度条和按钮区域 */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1">
            {/* 进度条 */}
            <div className="mb-2">
              <Progress
                percent={Math.min((now_number / max_number) * 100, 100)}
                style={{ height: '6px', backgroundColor: progress_bar_background_color }}
                barStyle={{ backgroundColor: progress_bar_main_color }}
              />
            </div>

            {/* 底部文字信息 */}
            <div className="flex items-center enki-body-2xs-medium">
              {content?.content && <span className="text-promo-200-fg-minor mr-1">{content.content}</span>}
              {content?.content && content?.desc && <span className="text-surface-100-fg-minor mr-1">•</span>}
              {content?.desc && <span className="text-surface-100-fg-minor">{content.desc}</span>}
            </div>
          </div>

          {/* 按钮 */}
          <div className="flex-shrink-0">
            <SharePopup
              data={shareData}
              appShareEnable={false}
              showFbHeadConfig={false}
              onBeforeShow={show => {
                shareClick({
                  co: {
                    target_nm: 'invite_your_friends',
                    target_type: ClickTargetType.shareButton,
                    click_type: ClickType.share
                  }
                });
                show();
              }}
              saveImageClick={() => {
                shareImageRef.current.show();
              }}
              render={() => {
                return (
                  <Button
                    type="primary"
                    size="xs"
                    className="enki-body-base-medium text-reserved-true-white !bg-btn-secondary-bg">
                    {content?.button_text}
                  </Button>
                );
              }}
            />
            <ShareReferralImage
              ref={shareImageRef}
              extraParams={{
                type: 'student'
              }}
              onClose={() => {
                shareImageRef.current.hide();
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExclusiveEvent;
